import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { GapRow } from '../../ui/Gaps'
import { formatDayTime } from '@dorixona/utils'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { Icon } from '../../ui/Icon'
import { DetailHead } from '../../ui/DetailHead'
import { Failed, Loading, failureText } from '../../ui/Load'
import { Select } from '../../ui/Select'
import { TableSkeleton } from '../../ui/Skeleton'
import { Pagination } from '../../ui/Pagination'
import { useSession } from '../auth/session'
import {
  channelsDraft,
  useExchangeLog,
  useIntegrations,
  usePaymentChannels,
  useSetPaymentChannels,
  useSetProvider,
  useTestIntegration,
  type ChannelsDraft,
  type Integration,
} from './api'
import { PaymentChannelsCard } from './PaymentChannels'
import { MapKeys } from './MapKeys'
import { env } from '../../config/env'
import {
  HEALTH_TEXT,
  LOG_PAGE_SIZES,
  MAPS_INTEGRATION,
  SUPER_ADMIN_ROLE,
  errorsLabel,
  groupName,
  health,
  providerText,
  type Health,
} from './model'

export interface IntegrationScreenProps {
  onToast: (message: string) => void
}

type Tab = 'set' | 'log'

/** Сколько последних обменов видно рядом со сводкой. */
const RECENT = 5

/**
 * Тон состояния — тот же, что в списке интеграций: зелёный — работает
 * по-настоящему, заглушка — info, выключенная — нейтральная.
 */
const HEALTH_TONE: Record<Health, string | undefined> = {
  ok: 'success',
  stub: 'info',
  off: undefined,
}

/** Карточка интеграции: на чём работает и что уходило наружу. */
export function IntegrationScreen({ onToast }: IntegrationScreenProps) {
  const navigate = useNavigate()
  const params = useParams()
  const key = params.id ?? ''
  const integrations = useIntegrations()

  if (integrations.isPending) return <Loading what="интеграцию" />
  if (integrations.isError) {
    return (
      <Failed
        what="интеграцию"
        error={integrations.error}
        onRetry={() => {
          void integrations.refetch()
        }}
      />
    )
  }

  const integration = integrations.data.find((i) => i.key === key)
  if (!integration) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-plug" />
          <b>Интеграция не найдена</b>
          <p>Возможно, её отключили от проекта.</p>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/integrations')
            }}
          >
            К списку интеграций
          </button>
        </div>
      </div>
    )
  }

  // Ключ — ключ интеграции: другая интеграция начинается с чистого черновика.
  return <IntegrationCard key={integration.key} integration={integration} onToast={onToast} />
}

interface IntegrationCardProps {
  integration: Integration
  onToast: (message: string) => void
}

/**
 * Карточка загруженной интеграции. Выбор провайдера — форма: он копится в
 * черновике и уходит кнопкой «Сохранить», чтобы случайный выбор в списке не
 * выключил оплату. Проверка связи — действие, она уходит сразу.
 */
function IntegrationCard({ integration, onToast }: IntegrationCardProps) {
  const { can, me } = useSession()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('set')
  const setProvider = useSetProvider()
  // У платежей, кроме провайдера, есть каналы оплаты (R-113).
  const isPayment = integration.key === 'payment'
  // У карт — пул ключей Яндекса (флаг `VITE_FEATURE_MAPS_KEYS`).
  const hasMapKeys = env.features.mapsKeys && integration.key === MAPS_INTEGRATION
  const channels = usePaymentChannels(isPayment)
  const setChannels = useSetPaymentChannels()
  const test = useTestIntegration()
  // Сводка и «последние обмены» — первая страница журнала (самые новые).
  // Вкладка журнала листает свою; на первой странице это один и тот же запрос.
  const recent = useExchangeLog(integration.key)
  const [logPage, setLogPage] = useState(1)
  const [logSize, setLogSize] = useState<number>(LOG_PAGE_SIZES[0])
  const log = useExchangeLog(integration.key, logPage, logSize)
  // null — правок нет, форма показывает провайдера с сервера.
  const [edits, setEdits] = useState<string | null>(null)

  const provider = edits ?? integration.provider
  const providerDirty = edits !== null && edits !== integration.provider
  const [channelEdits, setChannelEdits] = useState<ChannelsDraft | null>(null)
  const savedChannels = channels.data ? channelsDraft(channels.data) : null
  const channelDraft = channelEdits ?? savedChannels
  const channelsDirty =
    channelEdits !== null &&
    savedChannels !== null &&
    JSON.stringify(channelEdits) !== JSON.stringify(savedChannels)
  const dirty = providerDirty || channelsDirty
  const saving = setProvider.isPending || setChannels.isPending

  // Уход со страницы с несохранённым провайдером браузер переспросит.
  useUnsavedWarning(dirty)

  const canUpdate = can('integrations', 'u')
  const state = health(integration)
  const latest = recent.data?.items ?? []
  // Всего обменов — итог сервера, а не длина страницы.
  const total = recent.data?.total ?? 0
  // Фильтра по статусу у журнала нет, поэтому ошибки считаем только среди
  // пришедших строк и так и подписываем.
  const errors = latest.filter((e) => !e.ok).length
  const events = log.data?.items ?? []
  const tabs: [Tab, string][] = [
    ['set', 'Провайдер'],
    ['log', 'Журнал обмена'],
  ]

  function checkLink() {
    test.mutate(integration.key, {
      onSuccess: (result) => {
        onToast(
          result.ok
            ? `${integration.title}: связь есть`
            : `${integration.title}: ${result.message || 'связи нет'}`,
        )
      },
      onError: (error) => {
        onToast(failureText(error))
      },
    })
  }

  function saveAll() {
    const failed = (error: unknown) => {
      onToast(failureText(error))
    }
    if (providerDirty) {
      setProvider.mutate(
        { key: integration.key, provider },
        {
          onSuccess: () => {
            setEdits(null)
            onToast(`${integration.title}: ${providerText(provider)}`)
          },
          onError: failed,
        },
      )
    }
    if (channelsDirty) {
      setChannels.mutate(channelEdits, {
        onSuccess: () => {
          setChannelEdits(null)
          onToast('Каналы оплаты сохранены')
        },
        onError: failed,
      })
    }
  }

  return (
    <>
      <DetailHead
        backLabel="Интеграции"
        onBack={() => {
          void navigate('/integrations')
        }}
        title={integration.title}
        sub={
          <>
            <span className="ui-status" data-tone={HEALTH_TONE[state]}>
              {HEALTH_TEXT[state]}
            </span>
            <span>{groupName(integration.key)}</span>
            <span className="dot" aria-hidden="true" />
            <span>{providerText(integration.provider)}</span>
          </>
        }
        actions={
          canUpdate ? (
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              disabled={test.isPending}
              aria-busy={test.isPending}
              onClick={checkLink}
            >
              <Icon name="i-sync" />
              Проверить связь
            </button>
          ) : (
            <span className="ui-tag">только просмотр</span>
          )
        }
      />

      <div className="ui-tabs ui-detail-tabs" role="tablist" aria-label="Разделы интеграции">
        {tabs.map(([id, name]) => (
          <button
            key={id}
            type="button"
            role="tab"
            className="ui-tab"
            aria-selected={tab === id}
            onClick={() => {
              setTab(id)
            }}
          >
            {name}
            {id === 'log' ? <span className="ui-count">{total}</span> : null}
            {/* Выбор не пропадает при переходе в журнал — точка напоминает,
                что он ждёт сохранения. */}
            {id === 'set' && dirty ? (
              <span className="ui-dirty-dot" aria-label="есть несохранённые изменения" />
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'set' ? (
        <>
          <div className="ui-detail-grid">
            <div className="ui-detail-col">
              <section className="ui-card" aria-labelledby="int-work">
                <div className="ui-card-head">
                  <h3 id="int-work">На чём работает</h3>
                  <span className="aside">
                    <Icon name="i-key" />
                    ключи — в окружении сервера
                  </span>
                </div>
                <div className="ui-card-body ui-form">
                  <div className="ui-field">
                    <span className="ui-label" aria-hidden="true">
                      Провайдер
                    </span>
                    <Select
                      label="Провайдер"
                      value={provider}
                      disabled={!canUpdate}
                      options={integration.options.map((option) => ({
                        value: option,
                        label: providerText(option),
                      }))}
                      onChange={(next) => {
                        setEdits(next)
                      }}
                    />
                    <span className="ui-hint">
                      {integration.note || 'Дополнительных условий нет'}
                    </span>
                  </div>
                  <div className="ui-facts">
                    <div>
                      <span className="k">Настройка</span>
                      <span className="v">
                        <span className="ui-code">{integration.setting}</span>
                      </span>
                    </div>
                    <div>
                      <span className="k">Включена</span>
                      <span className="v">{integration.enabled ? 'да' : 'нет'}</span>
                    </div>
                    <GapRow what="Ключи и адрес вебхука" where="GET /admin/integrations" />
                  </div>
                </div>
              </section>

              {isPayment && channels.isError ? (
                <Failed
                  what="каналы оплаты"
                  error={channels.error}
                  onRetry={() => {
                    void channels.refetch()
                  }}
                />
              ) : null}
              {isPayment && channels.data && channelDraft ? (
                <PaymentChannelsCard
                  channels={channels.data}
                  draft={channelDraft}
                  editable={canUpdate && me?.roleId === SUPER_ADMIN_ROLE}
                  onChange={setChannelEdits}
                />
              ) : null}
            </div>

            <div className="ui-detail-col">
              <section className="ui-card" aria-labelledby="int-how">
                <div className="ui-card-head">
                  <h3 id="int-how">Как работает</h3>
                </div>
                <div className="ui-card-body int-facts-body">
                  <div className="ui-facts">
                    <div>
                      <span className="k">Обменов в журнале</span>
                      <span className="v num">{total}</span>
                    </div>
                    <div>
                      <span className="k">{errorsLabel(latest.length, total)}</span>
                      <span className="v num" data-bad={errors ? true : undefined}>
                        {errors}
                      </span>
                    </div>
                    <div>
                      <span className="k">Последний обмен</span>
                      <span className="v num">
                        {latest[0] ? formatDayTime(latest[0].at) : 'не было'}
                      </span>
                    </div>
                    <GapRow
                      what="Время ответа"
                      where="Exchange · GET /admin/integrations/{key}/log"
                    />
                  </div>
                </div>
              </section>

              {latest.length > 0 ? (
                <section className="ui-card" aria-labelledby="int-recent">
                  <div className="ui-card-head">
                    <h3 id="int-recent">Последние обмены</h3>
                    <button
                      type="button"
                      className="ui-btn"
                      data-variant="ghost"
                      data-size="sm"
                      onClick={() => {
                        setTab('log')
                      }}
                    >
                      Весь журнал
                    </button>
                  </div>
                  <div className="ui-card-body int-facts-body">
                    <div className="ui-facts">
                      {latest.slice(0, RECENT).map((event) => (
                        <div key={event.id}>
                          <span className="k num">{formatDayTime(event.at)}</span>
                          <span className="v int-recent">
                            <span className="ui-code">{event.operation}</span>
                            <span className="ui-status" data-tone={event.ok ? 'success' : 'danger'}>
                              {event.ok ? 'успешно' : 'ошибка'}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              ) : null}
            </div>
          </div>

          {hasMapKeys ? <MapKeys onToast={onToast} /> : null}

          {canUpdate ? (
            <div className="ui-savebar" data-dirty={dirty || undefined}>
              <span className="state" role="status">
                {dirty ? 'Есть несохранённые изменения' : 'Все изменения сохранены'}
              </span>
              {dirty ? (
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="secondary"
                  disabled={saving}
                  onClick={() => {
                    setEdits(null)
                    setChannelEdits(null)
                  }}
                >
                  Отменить
                </button>
              ) : null}
              <button
                type="button"
                className="ui-btn"
                disabled={!dirty || saving}
                aria-busy={saving}
                onClick={saveAll}
              >
                Сохранить
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {tab === 'log' ? (
        <section className="ui-table-card" aria-labelledby="int-log">
          <div className="ui-card-head">
            <h3 id="int-log">Журнал обмена</h3>
            <span className="aside">
              <Icon name="i-key" />
              без ключей и личных данных
            </span>
          </div>
          {log.isPending ? <TableSkeleton what="журнал" rows={4} /> : null}
          {log.isError ? (
            <Failed
              what="журнал"
              error={log.error}
              onRetry={() => {
                void log.refetch()
              }}
            />
          ) : null}
          {log.data?.total === 0 ? (
            <div className="ui-empty">
              <Icon name="i-sync" />
              <b>Обменов с этой интеграцией ещё не было</b>
            </div>
          ) : null}
          {log.data && log.data.total > 0 ? (
            <>
              <div className="ui-table-wrap" aria-busy={log.isPlaceholderData}>
                <table className="ui-table int-log">
                  <colgroup>
                    <col className="w-at" />
                    <col className="w-result" />
                    <col className="w-op" />
                    <col />
                    <col className="w-order" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">Когда</th>
                      <th scope="col">Результат</th>
                      <th scope="col">Операция</th>
                      <th scope="col">Сообщение</th>
                      <th scope="col" data-align="end">
                        Заказ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event) => (
                      <tr key={event.id}>
                        <td className="num">{formatDayTime(event.at)}</td>
                        <td>
                          <span className="ui-status" data-tone={event.ok ? 'success' : 'danger'}>
                            {event.ok ? 'успешно' : 'ошибка'}
                          </span>
                        </td>
                        <td>
                          <span className="ui-code">{event.operation}</span>
                        </td>
                        <td className="int-msg">{event.message || '—'}</td>
                        <td className="num" data-align="end">
                          {event.orderId || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                label="Страницы журнала"
                page={logPage}
                pageSize={logSize}
                total={log.data.total}
                sizes={LOG_PAGE_SIZES}
                busy={log.isPlaceholderData}
                onPage={setLogPage}
                onPageSize={(size) => {
                  setLogSize(size)
                  setLogPage(1)
                }}
              />
            </>
          ) : null}
        </section>
      ) : null}
    </>
  )
}
